import { IPromise, IRootScopeService } from "angular";
import { LinkService } from "@gtmhub/links";
import { IMetricLinksChangedEventArgs, OkrEvents } from "@gtmhub/okrs/events";
import { IUpdateItemLinks } from "@webapp/links/models/links-api.models";
import { CascadingLink, ICanLinkInfo, LinksDTO } from "@webapp/links/models/links.models";
import { generateMetricLinksSpec } from "../utils";

export class MetricLinkService {
  private okrEvents: OkrEvents;

  public static $inject = ["$rootScope", "LinkService"];

  constructor($rootScope: IRootScopeService, private linkService: LinkService) {
    this.okrEvents = new OkrEvents($rootScope);
  }

  public link(goalId: string, sourceId: string, linkType: CascadingLink, targetId: string): IPromise<void> {
    return this.linkService.link({ sourceType: "metrics", sourceId, linkType, targetType: "metrics", targetId }).then(() => {
      const link: LinksDTO<"cascading"> = { sourceId, targetId, linkType, sourceType: "metrics", targetType: "metrics" };
      this.okrEvents.broadcastGoalsChanged("metricLinked", { goalId, link });
    });
  }

  public unlink(goalId: string, sourceId: string, linkType: CascadingLink, targetId: string): IPromise<void> {
    return this.linkService.unlink({ sourceType: "metrics", sourceId, linkType, targetType: "metrics", targetId }).then(() => {
      const link: LinksDTO<CascadingLink> = { sourceId, targetId, linkType, sourceType: "metrics", targetType: "metrics" };
      this.okrEvents.broadcastGoalsChanged("metricUnlinked", { goalId, link });
    });
  }

  public bulkLink(args: {
    goalId: string;
    sourceMetricId: string;
    linkType: CascadingLink;
    targetMetricIds: string[];
    shouldSkipDueDatesUpdates: boolean;
  }): IPromise<void> {
    const { goalId, sourceMetricId, linkType, targetMetricIds, shouldSkipDueDatesUpdates } = args;
    const bulkBody = generateMetricLinksSpec(linkType, targetMetricIds);

    return this.linkService.bulkLink({ sourceType: "metrics", sourceId: sourceMetricId, body: bulkBody, shouldSkipDueDatesUpdates }).then(() => {
      this.broadcastLinks("metricLinked", { targetIds: targetMetricIds, linkType, sourceId: sourceMetricId, goalId });
    });
  }

  public patchLinks(args: {
    goalId: string;
    sourceMetricId: string;
    linkType: CascadingLink;
    entityIdsToLink: string[];
    entityIdsToUnlink: string[];
    shouldSkipDueDatesUpdates: boolean;
  }): IPromise<void> {
    const { goalId, sourceMetricId, linkType, entityIdsToLink, entityIdsToUnlink, shouldSkipDueDatesUpdates } = args;
    const patchLinksBody: IUpdateItemLinks = {};

    if (entityIdsToLink.length) {
      patchLinksBody.linksToCreate = generateMetricLinksSpec(linkType, entityIdsToLink);
    }

    if (entityIdsToUnlink.length) {
      patchLinksBody.linksToDelete = generateMetricLinksSpec(linkType, entityIdsToUnlink);
    }

    return this.linkService.patchLinks({ sourceType: "metrics", sourceId: sourceMetricId, body: patchLinksBody, shouldSkipDueDatesUpdates }).then(() => {
      this.broadcastLinks("metricLinked", { targetIds: entityIdsToLink, linkType, sourceId: sourceMetricId, goalId });
      this.broadcastLinks("metricUnlinked", { targetIds: entityIdsToUnlink, linkType, sourceId: sourceMetricId, goalId });
    });
  }

  public checkIfCanLink(args: { sourceMetricId: string; linkType: CascadingLink; targetMetricIds: string[] }): IPromise<ICanLinkInfo> {
    const { sourceMetricId, linkType, targetMetricIds } = args;
    const body = generateMetricLinksSpec(linkType, targetMetricIds);

    return this.linkService.checkIfCanLink({ sourceType: "metrics", sourceId: sourceMetricId, body: body });
  }

  private broadcastLinks(reason: IMetricLinksChangedEventArgs["reason"], args: { targetIds: string[]; linkType: CascadingLink; sourceId: string; goalId: string }): void {
    const { targetIds, linkType, sourceId, goalId } = args;

    targetIds.forEach((targetId) => {
      const link: LinksDTO<CascadingLink> = { sourceId, targetId, linkType, sourceType: "metrics", targetType: "metrics" };
      this.okrEvents.broadcastGoalsChanged(reason, { goalId, link });
    });
  }
}
